import React, { useState, useEffect } from "react";
import {
    Button,
    Card,
    Collapse,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    IconButton,
    ListItem,
    ListItemIcon,
    ListItemText,
    Slide,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { AddCircle, Delete } from "@mui/icons-material";
import StartDate from "../StartDate/StartDate";
import EndDate from "../EndDate/EndDate";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { useTranslation } from "react-i18next";
import { TransitionGroup } from "react-transition-group";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import moment from "moment";

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

export default function AddShiftPlanPopup(props) {
    const defaultShiftPlan = {
        name: "",
        startDate: props.defaultStartDate,
        endDate: new Date("2099-12-31"),
        shifts: [
            {
                id: Math.floor(Math.random() * 100000),
                startingHour: new Date().setHours(0, 0),
                endingHour: new Date().setHours(7, 59),
            },
            {
                id: Math.floor(Math.random() * 100000),
                startingHour: new Date().setHours(8, 0),
                endingHour: new Date().setHours(15, 59),
            },
            {
                id: Math.floor(Math.random() * 100000),
                startingHour: new Date().setHours(16, 0),
                endingHour: new Date().setHours(23, 59),
            },
        ],
    };

    const { t } = useTranslation("translation");
    const [isAdd, setIsAdd] = useState(props.shiftPlan ? false : true);
    const [shiftPlan, setShiftPlan] = useState(props.shiftPlan ?? defaultShiftPlan);

    useEffect(() => {
        setIsAdd(props.shiftPlan ? false : true);
        setShiftPlan(props.shiftPlan ?? defaultShiftPlan);
    }, [props.shiftPlan]);

    // Set start date
    const HandleStartDate = (date) => {
        setShiftPlan({ ...shiftPlan, startDate: date });
    };

    // Set end date
    const HandleEndDate = (date) => {
        date.setHours(23, 59);
        setShiftPlan({ ...shiftPlan, endDate: date });
    };

    // Set time
    const HandleTime = (newTime, item, isstartingHour) => {
        const newShifts = shiftPlan.shifts.map((shift) => {
            if (shift.id === item.id) {
                let isBetween = false;
                const otherShifts = shiftPlan.shifts.filter((element) => element.id !== item.id);
                for (const element of otherShifts) {
                    const newTimeTotalMinutes = newTime.getHours() * 60 + newTime.getMinutes();
                    const startingTotalMinutes = moment(element.startingHour).hours() * 60 + moment(element.startingHour).minutes();

                    let endingTotalMinutes = moment(element.endingHour).hours() * 60 + moment(element.endingHour).minutes();
                    if (moment(element.endingHour).format("HH:mm") === "00:00") {
                        endingTotalMinutes = 24 * 60;
                    }

                    if (newTimeTotalMinutes > startingTotalMinutes && newTimeTotalMinutes < endingTotalMinutes) {
                        isBetween = true;
                    }
                }

                if (isBetween) return shift;
                return isstartingHour ? { ...shift, startingHour: newTime } : { ...shift, endingHour: newTime };
            }
            return shift;
        });
        setShiftPlan({ ...shiftPlan, shifts: newShifts });
    };

    const renderItem = (item, index) => {
        return (
            <ListItem
                sx={{ margin: 0.5 }}
                secondaryAction={
                    <Tooltip title={t("Delete")}>
                        <IconButton id={item.id} onClick={() => handleRemove(item)}>
                            <Delete />
                        </IconButton>
                    </Tooltip>
                }
            >
                <ListItemIcon>
                    <Typography>{t("Shift") + " " + (index + 1)}</Typography>
                </ListItemIcon>
                <ListItemText
                    primary={
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <Grid container spacing={2} marginRight={1} display="flex" alignItems="center" justifyContent="center">
                                <Grid id={`startShiftGrd_${index}`} item xs={5}>
                                    <TimePicker
                                        label={t("StartHour")}
                                        value={item.startingHour}
                                        ampm={false}
                                        onChange={(newTime) => HandleTime(newTime, item, true)}
                                        renderInput={(params) => <TextField id={`shifStart_${index}`} {...params} size="small" onKeyDown={(e) => e.preventDefault()} />}
                                    />
                                </Grid>
                                <Grid id={`endShiftGrd_${index}`} item xs={5}>
                                    <TimePicker
                                        label={t("EndHour")}
                                        value={item.endingHour}
                                        ampm={false}
                                        onChange={(newTime) => HandleTime(newTime, item, false)}
                                        renderInput={(params) => <TextField id={`shifEnd_${index}`} {...params} size="small" onKeyDown={(e) => e.preventDefault()} />}
                                    />
                                </Grid>
                            </Grid>
                        </LocalizationProvider>
                    }
                />
            </ListItem>
        );
    };

    const handleAdd = () => {
        const newShifts = shiftPlan.shifts;
        newShifts.push({
            //generate random id
            id: Math.floor(Math.random() * 100000),
            startingHour: new Date().setHours(0, 0),
            endingHour: new Date().setHours(0, 0),
        });
        setShiftPlan({ ...shiftPlan, shifts: newShifts });
    };

    const handleRemove = (item) => {
        const newShifts = shiftPlan.shifts.filter((shift) => shift.id !== item.id);
        setShiftPlan({ ...shiftPlan, shifts: newShifts });
    };

    return (
        <Container id="asp_Container">
            <Dialog id="asp_Dialog" open={props.open} onClose={props.onCancel} TransitionComponent={Transition}>
                <DialogTitle id="asp_DialogTitle">{isAdd ? t("Add") : t("Edit")}</DialogTitle>
                <DialogContent id="asp_DialogContent">
                    <Grid id="asp_Grid_3" container display="flex" alignItems="center" justifyContent="center" marginTop={1}>
                        <Grid id="asp_Grid_4" item xs={6}>
                            <FormControl id="asp_FormControl" fullWidth>
                                <TextField
                                    id="asp_TextField_Name"
                                    label={t("Name")}
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={shiftPlan.name}
                                    onChange={(e) => setShiftPlan({ ...shiftPlan, name: e.target.value })}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <Grid id="asp_Grid_5" container display="flex" justifyContent="center" alignItems="center" spacing={2} marginTop={2}>
                        <Grid id="asp_Grid_6" item>
                            <StartDate startDate={shiftPlan.startDate} HandleStartDate={HandleStartDate} />
                        </Grid>
                        <Grid id="asp_Grid_7" item>
                            <EndDate startDate={shiftPlan.startDate} endDate={shiftPlan.endDate} HandleEndDate={HandleEndDate} />
                        </Grid>
                    </Grid>
                    <Grid id="asp_Grid_8" container display="flex" justifyContent="center" alignItems="center" marginTop={4}>
                        <Card id="asp_List_9" sx={{ width: "90%", border: "1px solid #e0e0e0", borderRadius: "10px", padding: 0.5 }}>
                            <TransitionGroup>
                                {shiftPlan.shifts.map((item, index) => (
                                    <Collapse key={index}>{renderItem(item, index)}</Collapse>
                                ))}
                            </TransitionGroup>
                        </Card>
                    </Grid>
                    <Grid id="asp_Grid_10" container display="flex" justifyContent="center" alignItems="center" marginTop={0.5}>
                        <ListItemIcon>
                            <Tooltip title={t("Add")}>
                                <IconButton id="asp_IconButton" onClick={() => handleAdd()}>
                                    <AddCircle fontSize="large" />
                                </IconButton>
                            </Tooltip>
                        </ListItemIcon>
                    </Grid>
                </DialogContent>
                <DialogActions id="asp_DialogActions">
                    <Button id="asp_Button_Cancel" onClick={props.onCancel} color="error">
                        {t("Cancel")}
                    </Button>
                    <Button id="asp_Button_Add" onClick={() => props.onAdd(shiftPlan, isAdd)} color="primary">
                        {isAdd ? t("Add") : t("Edit")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
}
